/**
 * Super-admin pipeline-run observability page (Phase 2b).
 *
 * Smart route: `requireAdmin` beforeLoad guard, TanStack Query against the
 * raw Eden admin client (`/api/admin/pipeline-runs`), local selection state.
 * The dumb `PipelineRunsView` owns all rendering.
 */

import { useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useCallback, useState } from "react"
import { extractErrorMessage } from "../../lib/api/errors"
import { fetchPipelineRunDetail, fetchPipelineRuns } from "../../lib/api/pipeline-runs"
import { queryKeys } from "../../lib/query-keys"
import { requireAdmin } from "../../lib/route-guards"
import { PipelineRunsView } from "./-pipeline-runs/PipelineRunsView"
import { DETAIL_POLL_MS, hasAnyRunningRun, isRunLive, LIST_POLL_MS } from "./-pipeline-runs/PipelineRunsView/helpers"

const LIST_LIMIT = 50

export const Route = createFileRoute("/_auth/pipeline-runs")({
  beforeLoad: requireAdmin,
  component: PipelineRunsPage,
})

function PipelineRunsPage(): React.JSX.Element {
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null)
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)

  const listQuery = useQuery({
    queryKey: queryKeys.admin.pipelineRuns(LIST_LIMIT, 0),
    queryFn: () => fetchPipelineRuns(LIST_LIMIT, 0),
    // Poll while any listed run is still executing; stop once the page is settled.
    refetchInterval: (query) => (hasAnyRunningRun(query.state.data?.rows) ? LIST_POLL_MS : false),
  })

  const detailQuery = useQuery({
    queryKey: queryKeys.admin.pipelineRunDetail(selectedRunId ?? ""),
    queryFn: () => fetchPipelineRunDetail(selectedRunId ?? ""),
    enabled: selectedRunId !== null && selectedRunId.length > 0,
    // Poll only the selected in-flight run; stop on complete/failed.
    refetchInterval: (query) => (isRunLive(query.state.data?.run.status) ? DETAIL_POLL_MS : false),
  })

  const handleSelectRun = useCallback((runId: string | null): void => {
    setSelectedRunId(runId)
    setSelectedNodeId(null)
  }, [])

  const handleSelectNode = useCallback((nodeId: string | null): void => {
    setSelectedNodeId(nodeId)
  }, [])

  return (
    <PipelineRunsView
      runs={listQuery.data?.rows ?? []}
      isLoading={listQuery.isLoading}
      isError={listQuery.isError}
      errorMessage={listQuery.error ? extractErrorMessage(listQuery.error) : null}
      selectedRunId={selectedRunId}
      selectedDetail={detailQuery.data ?? null}
      isDetailLoading={detailQuery.isLoading}
      isDetailError={detailQuery.isError}
      detailErrorMessage={detailQuery.error ? extractErrorMessage(detailQuery.error) : null}
      onSelectRun={handleSelectRun}
      selectedNodeId={selectedNodeId}
      onSelectNode={handleSelectNode}
    />
  )
}
